<template>
    <div class="AddressSpot">
        <label
            class="AddressSpot-label"
            for="spot"
        />
        <div class="AddressSpot-inputContainer">
            <span class="f2m-search left" />
            <span
                class="f2m-gps-location right"
                @click="getCurrentLocation"
                @keyup.enter="getCurrentLocation"
            />
            <div class="AddressSpot-pipe">
                |
            </div>
            <input
                id="spot"
                v-model="currentValue"
                class="AddressSpot-input"
                type="text"
                :placeholder="label"
                @input="updateValue"
            >
        </div>

        <div
            v-if="itemsFormatted"
            class="AddressSpot-optionsContainer"
        >
            <input-options
                :avoidFocus="true"
                :isOptionsOpen="true"
                :items="itemsFormatted"
                :optionsObject="false"
                :value="itemsFormatted"
                :styleItemSelected="{
                    backgroundColor:'transparent',
                    color: 'white',
                    fontWeight: 'bold'
                }"
                :styleItem="{
                    borderBottom: '1px solid rgba(255,255,255, .2)',
                    fontSize: '22px',
                    lineHeight: '26px',
                    height: '57px',
                    alignItems: 'center'
                }"
                :withSlotItem="true"
                @input="optionChanged"
            >
                <template #option="{item}">
                    <AddressSpotItem
                        data-cy="option.addressItem"
                        :item="item"
                    />
                </template>
            </input-options>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash/debounce';
import InputOptions from '@/components/AddressInputOptions.vue'
import AddressSpotItem from "@/components/AddressSpotItem.vue";
import * as mutationTypes from "@/store/mutation-types";
import {SpotApi} from "@free2move/open-api-spot-client";

export default {
    components: {
        AddressSpotItem,
        InputOptions
    },
    props: {
        label: {
            type: String,
            required: true,
        },
        country: {
            type: String,
            default: ''
        },
        language: {
            type: String,
            default: ''
        },
        required: {
            type: Boolean,
        },
        readonly: {
            type: Boolean,
        },
        value: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            debouncedRequest: debounce(this.request, 250),
            isValid: true,
            items: [],
            itemsFormatted: [],
            active: null,
            tempValue: '',
        };
    },
    computed: {
        currentValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val)
            }
        },
    },
    methods: {
        getCurrentLocation(){
            if(navigator.geolocation) { // if the browser supports geolocation
                this.$store.commit(mutationTypes.UPDATE_SHOW_TRANSPARENT_GLOBAL_LOADER, true);
                navigator.geolocation.getCurrentPosition(
                    (data) => {
                        axios
                            .get('/api/spots/geocode', {
                                params: {
                                    sources: ['gm', 'place'],
                                    latitude: data.coords.latitude,
                                    longitude: data.coords.longitude,
                                    country: this.country,
                                    language: this.language,
                                },
                            })
                            .then((res) => {
                                if (res.data.length) {
                                    this.active = null;
                                    this.items = res.data;
                                    this.itemsFormatted = res.data.map((item) => item.name).slice(0, 5);
                                }
                            })
                            .catch()
                            .finally(() => {
                                this.$store.commit(mutationTypes.UPDATE_SHOW_TRANSPARENT_GLOBAL_LOADER, false);
                            })
                    },
                    () => this.$store.commit(mutationTypes.UPDATE_SHOW_TRANSPARENT_GLOBAL_LOADER, false)
                )
            } else {
                alert('Your browser does not support geolocation') // if the browser doesn't support geolocation
            }
        },
        updateValue(event) {
            if (this.itemsFormatted.includes(event.target.value)) {
                const index = this.itemsFormatted.indexOf(event.target.value);
                this.tempValue = event.target.value;
                const item = this.items[index];
                this.$emit('addressSelected', item);
            } else {
                this.tempValue = event.target.value;
                this.debouncedRequest(event.target.value);
            }
        },
        request(value) {
            if (!value) {
                this.items = [];
                this.itemsFormatted = [];
                return;
            }
            new SpotApi().spotSearch({
                query: value,
                sources: ['gm', 'place'],
                types: 'address',
            })
                .then((res) => {
                    if (res.data.length) {
                        this.active = null;
                        this.items = res.data;
                        this.itemsFormatted = res.data.map((item) => item.name).slice(0, 5);
                    }
                })
                .catch()
                .finally(() => {
                    this.$store.commit(mutationTypes.UPDATE_SHOW_TRANSPARENT_GLOBAL_LOADER, false);
                })
        },
        optionChanged(selectedIndex) {
            this.$emit('addressSelected', this.items[selectedIndex]);
        }
    },
};
</script>
<style lang="scss" scoped>
.AddressSpot {
    position: relative;

    &-label {
        display: none;
    }

    &-inputContainer{
        margin-top: 65px;
        position: relative;
        height: 46px;
        background: #FFFFFF;
        box-shadow: 0px 2px 8px rgba(126, 106, 153, 0.3);
        border: none;
        border-radius: 24px;
        width: 100%;
    }

    &-pipe{
        position: absolute;
        left:45px;
        color: #573781;
        font-style: normal;
        font-weight: 400;
        font-size: 22px;
        line-height: 46px;
        letter-spacing: 0.33px;
        height: 100%;
        margin: 0;
    }

    span{
        font-size: 24px;

        &.left{
            position: absolute;
            padding: 12px 12px;
            pointer-events: none;
        }

        &.right {
            position: absolute;
            right: 0;
            padding: 12px 12px;
            color: $color-f2m-pink;
            cursor: pointer;
        }
    }


    &-input{
        width: 100%;
        padding:0 60px;
        background: transparent;
        color: $color-f2m-purple;
        font-style: normal;
        font-weight: 400;
        font-size: 22px;
        line-height: 46px;
        letter-spacing: 0.33px;
        border: none;
        border-radius: 24px;
    }

    &-optionsContainer {
        position: relative;
        width: 100%;
        ul {
            background: transparent;
            color:white;
            box-shadow: none;
            max-height: none;
        }
    }
}
</style>
